import useLocalStorage from "Shared/hooks/useLocalStorage"
import { useCallback, useEffect, useState } from "react"
import { QueryParamsFilter } from "@finway-group/shared/lib/models"
import { adjustTimeRangeOfDateFields, convertFilterObjectIntoQueryFormat } from "Shared/utils/filter.utils"

export const useFilter = <FilterType extends Record<string, any>>({ localStorageKey }: { localStorageKey: string }) => {
    const [filters, setFilters] = useLocalStorage<FilterType>(localStorageKey, {} as FilterType)
    const [filterQuery, setFilterQuery] = useState<QueryParamsFilter>({})
    const [isFilterModalShowing, setIsFilterModalShowing] = useState<boolean>(false)

    const showFilterModal = () => {
        setIsFilterModalShowing(true)
    }
    const hideFilterModal = () => {
        setIsFilterModalShowing(false)
    }

    useEffect(() => {
        localStorage.setItem(localStorageKey, JSON.stringify(filters))
        const newFilterQuery = convertFilterObjectIntoQueryFormat(filters, true)
        setFilterQuery(newFilterQuery)
    }, [filters])

    const onFilter = useCallback((newFilters: FilterType) => {
        const sanitized = { ...newFilters }

        // extend inside this function min/max params whenever we add a new minMax date component to filters
        adjustTimeRangeOfDateFields(sanitized)

        Object.keys(sanitized).forEach((key) => {
            if (sanitized[key] === undefined || sanitized[key] === null || sanitized[key] === "") {
                delete sanitized[key]
            }
        })

        setFilters(sanitized)
        hideFilterModal()
    }, [])

    const onRemoveFilter = useCallback(
        (filterKey: keyof FilterType) => {
            if (!filterKey) {
                setFilters({} as FilterType)
                return
            }
            const newFilters = { ...filters }
            delete newFilters[filterKey]

            setFilters(newFilters)
        },
        [filters],
    )

    return {
        filters,
        filterQuery,
        onFilter,
        onRemoveFilter,
        isFilterModalShowing,
        showFilterModal,
        hideFilterModal,
    }
}
